import { debounce, put, call, takeLatest } from 'redux-saga/effects'

import { USER_INBOX_DOMAIN } from 'Constants'
import {
  updateUser as updateUserApi,
  registerUser as registerUserApi,
  getUserProfile as getUserProfileApi,
  createUserInbox as createUserInboxApi,
  verifyAccountEmail as verifyAccountEmailApi,
  resendAccountVerificationEmail as resendAccountVerificationEmailApi,
} from 'Service/Api'

import {
  setUser as setUserInLocalStorage,
  getUser as getUserFromLocalStorage,
} from 'Service/localUser'

import * as UserTypes from 'Redux/Types/UserTypes'
import * as UserActions from 'Redux/Actions/UserActions'
import * as SessionActions from 'Redux/Actions/SessionActions'

const mergeUserUpdates = (profile, { country, email, industry, phone, ...updates }) => {
  const contactMethods = profile.contactMethods.map((item) => {
    if (item.type.name === 'UserPrimaryEmail') {
      return { ...item, value: email }
    }
    if (item.type.name === 'Phone') {
      return { ...item, value: phone }
    }
    return item
  })

  return {
    ...profile,
    ...updates,
    contactMethods,
    companyName: { name: updates.companyName },
    country: country.value,
    currency: country.value.currency,
    industry: industry.value,
    jobTitle: { name: updates.jobTitle },
  }
}

function* getLocalUser() {
  const user = getUserFromLocalStorage()
  if (user) {
    yield put(UserActions.getLocalUserSucceeded(user))
  } else {
    yield put(UserActions.getLocalUserFailed(null))
  }
}

function* registerUserByOauth2({ payload }) {
  try {
    const { data } = yield call(registerUserApi, payload)
    // TODO: We need to move this to API headers only
    global.localStorage.setItem('authToken', data.token)
    global.localStorage.setItem('authTokenExpirationDate', data.tokenExpiration)
    yield put(SessionActions.createSessionSucceeded(data))
  } catch (errors) {
    yield put(SessionActions.createSessionFailed())
  }
}

function* updateUser({ payload }) {
  try {
    const profile = getUserFromLocalStorage()
    const updatedUser = mergeUserUpdates(profile, payload)

    const { data } = yield call(updateUserApi, updatedUser)
    setUserInLocalStorage(data)
    yield put(UserActions.updateUserSucceeded(data))
  } catch (error) {
    yield put(UserActions.updateUserFailed())
  }
}

function* registerUser({ payload }) {
  const { params } = payload
  try {
    const { data } = yield call(registerUserApi, params)
    yield put(UserActions.registerUserSucceeded(data))
  } catch (error) {
    yield put(UserActions.registerUserFailed(JSON.parse(error.message)))
  }
}

function* verifyAccountEmail({ payload }) {
  try {
    yield call(verifyAccountEmailApi, payload)
    yield put(UserActions.verifyAccountEmailSucceeded())
  } catch (errors) {
    yield put(UserActions.verifyAccountEmailFailed(errors))
  }
}

function* getUserProfile({ payload }) {
  try {
    let user = getUserFromLocalStorage()
    if (!user.id || (payload && payload.remote)) {
      const { data } = yield call(getUserProfileApi, payload)
      user = data
      setUserInLocalStorage(user)
    }
    yield put(UserActions.getUserProfileSucceeded(user))
  } catch (errors) {
    yield put(UserActions.getUserProfileFailed(errors))
  }
}

function* createUserInbox({ payload }) {
  const { email, resolver, rejector } = payload

  try {
    const { data } = yield call(createUserInboxApi, {
      email: `${email}@${USER_INBOX_DOMAIN}`,
    })
    yield put(UserActions.createUserInboxSucceeded())

    if (resolver) {
      resolver(data)
    }
  } catch (errors) {
    const errorMesage = JSON.parse(errors.message)

    yield put(UserActions.createUserInboxFailed(errorMesage))

    if (rejector) {
      rejector(errorMesage)
    }
  }
}

function* resendAccountVerificationEmail({ payload }) {
  try {
    yield call(resendAccountVerificationEmailApi, payload)
    yield put(UserActions.resendAccountVerificationEmailSucceeded())
  } catch (errors) {
    yield put(UserActions.resendAccountVerificationEmailFailed(errors))
  }
}

export default function* sessionSaga() {
  yield takeLatest(UserTypes.CREATE_USER_INBOX, createUserInbox)
  yield takeLatest(UserTypes.GET_LOCAL_USER, getLocalUser)
  yield debounce(150, UserTypes.GET_USER_PROFILE, getUserProfile)
  yield takeLatest(UserTypes.REGISTER_USER_BY_OAUTH2, registerUserByOauth2)
  yield takeLatest(UserTypes.REGISTER_USER, registerUser)
  yield takeLatest(UserTypes.RESEND_ACCOUNT_VERIFICATION_EMAIL, resendAccountVerificationEmail)
  yield takeLatest(UserTypes.UPDATE_USER, updateUser)
  yield takeLatest(UserTypes.VERIFY_ACCOUNT_EMAIL, verifyAccountEmail)
}
